/**
 * Progressive WOW pricing — fires batches of `DEFAULT_PROGRESSIVE_BATCH_SIZE`
 * (10) cards as the user scrolls past the initial 100. Ported from
 * flights-pwa UIENG-6303 (`OnewayView`'s progressive WOW block).
 *
 * Architecture:
 *  - A single IntersectionObserver watches the FIRST element of the NEXT
 *    window (the "sentinel"). When that card enters the viewport, the next
 *    batch of 10 cards is fetched via `fetchDynamicContent`, the sentinel
 *    advances by 10, and we wait again. Cascades until all cards are covered.
 *  - `makeCardRef(travelOptionId, cardId, index)` returns a ref-callback the
 *    TuppleRenderer attaches to each card's outer container; we register the
 *    DOM node, and when the index matches the sentinel we (re)attach the IO.
 *  - On filter/sort changes, the `cardsFingerprint` mutates, the IO resets,
 *    and window 0 fires immediately.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'

import { fetchDynamicContent } from '@/api/srp'
import { DEFAULT_PROGRESSIVE_BATCH_SIZE } from '@/constants/flights'
import {
    addProgressiveLoadingIndices,
    getIsDynamicContentLoading,
    getWowFares,
    mergeWowFares,
    removeProgressiveLoadingIndices,
    setProgressiveLoadingIndices
} from '@/redux/slices/srpSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store/hooks'
import { getFaresInfo } from '@/utils/srp/helper'
import { getFetchContentPayload } from '@/utils/srp/wowPayload'

interface UseProgressiveWowPricingArgs {
    enabled: boolean
    /** OW card list (the only journey progressive WOW touches per UIENG-6303). */
    cards: any[]
    searchData: any
    processedData: { isIntl?: boolean; tripType?: string; isCfw?: boolean }
    showPerAdultPricing: boolean
    showCheckInBaggagePriceOW: boolean
    wowPricingVariant: string
}

export function useProgressiveWowPricing({
    enabled,
    cards,
    searchData,
    processedData,
    showPerAdultPricing,
    showCheckInBaggagePriceOW,
    wowPricingVariant
}: UseProgressiveWowPricingArgs) {
    const dispatch = useAppDispatch()
    const wowFares = useAppSelector(getWowFares)
    const isDynamicContentLoading = useAppSelector(getIsDynamicContentLoading)

    // Always-fresh refs so the IO callbacks read the latest values.
    const wowFaresRef = useRef(wowFares)
    wowFaresRef.current = wowFares
    const isDynamicContentLoadingRef = useRef(isDynamicContentLoading)
    isDynamicContentLoadingRef.current = isDynamicContentLoading
    const searchDataRef = useRef(searchData)
    searchDataRef.current = searchData
    const cardsRef = useRef(cards)
    cardsRef.current = cards
    const showCheckInBaggagePriceOWRef = useRef(showCheckInBaggagePriceOW)
    showCheckInBaggagePriceOWRef.current = showCheckInBaggagePriceOW
    const showPerAdultPricingRef = useRef(showPerAdultPricing)
    showPerAdultPricingRef.current = showPerAdultPricing
    const processedDataRef = useRef(processedData)
    processedDataRef.current = processedData
    const wowPricingVariantRef = useRef(wowPricingVariant)
    wowPricingVariantRef.current = wowPricingVariant

    // index → currently-mounted DOM element (used by IO).
    const indexElemMapRef = useRef<Map<number, HTMLElement>>(new Map())
    // First card of the NEXT window — the current sentinel.
    const sentinelIndexRef = useRef<number>(DEFAULT_PROGRESSIVE_BATCH_SIZE)
    const sentinelIORef = useRef<IntersectionObserver | null>(null)

    // ── attachSentinel ────────────────────────────────────────────────────
    const attachSentinelRef = useRef<(sentinelIndex: number) => void>(() => {})
    attachSentinelRef.current = (sentinelIndex: number) => {
        sentinelIORef.current?.disconnect()
        sentinelIORef.current = null

        const totalCards = cardsRef.current?.length ?? 0
        if (sentinelIndex >= totalCards) return

        const el = indexElemMapRef.current.get(sentinelIndex)
        if (!el) return // not yet mounted; makeCardRef will retry on mount

        // Already in viewport — fire immediately.
        const rect = el.getBoundingClientRect()
        if (rect.top < window.innerHeight && rect.bottom > 0) {
            fetchWindowRef.current(sentinelIndex)
            return
        }

        sentinelIORef.current = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return
                    sentinelIORef.current?.disconnect()
                    sentinelIORef.current = null
                    fetchWindowRef.current(sentinelIndex)
                })
            },
            { threshold: 0 }
        )
        sentinelIORef.current.observe(el)
    }

    // ── fetchWindow ───────────────────────────────────────────────────────
    const fetchWindowRef = useRef<(windowStart: number) => void>(() => {})
    fetchWindowRef.current = async (windowStart: number) => {
        if (isDynamicContentLoadingRef.current) return

        const sd = searchDataRef.current || {}
        const { subTravelOptions: sto, fares: f, configData } = sd
        const batchSize =
            configData?.progressiveWowSolutionsCount ?? DEFAULT_PROGRESSIVE_BATCH_SIZE
        const cardList = cardsRef.current || []

        type BatchEntry = { cardId: string; travelOptionId: string; index: number }
        const batch: BatchEntry[] = []

        for (let i = windowStart; i < windowStart + batchSize; i++) {
            const card = cardList[i]
            if (!card) break
            const faresObjectList =
                getFaresInfo(card, sto, f, false, showCheckInBaggagePriceOWRef.current) || []
            const firstFareId = faresObjectList?.[0]?.fareId
            // Already fetched — skip.
            if (firstFareId && wowFaresRef.current[firstFareId]) continue
            batch.push({
                cardId: card.cardId,
                travelOptionId: card.travelOptionId,
                index: i
            })
        }

        // Advance sentinel synchronously, BEFORE the async fetch — keeps the
        // IO chain responsive even if a single batch is empty.
        const nextSentinel = windowStart + batchSize
        sentinelIndexRef.current = nextSentinel
        attachSentinelRef.current(nextSentinel)

        if (!batch.length) return

        const indices = batch.map(b => b.index)
        dispatch(addProgressiveLoadingIndices(indices))
        try {
            const overrideCards = {
                J1: batch.map(b => ({ cardId: b.cardId, travelOptionId: b.travelOptionId }))
            }
            const payload = getFetchContentPayload(searchDataRef.current, {
                isIntl: !!processedDataRef.current?.isIntl,
                tripType: processedDataRef.current?.tripType,
                isCfw: !!processedDataRef.current?.isCfw,
                wowPricingVariant: wowPricingVariantRef.current,
                overrideCards
            })
            const response = await fetchDynamicContent(payload, !!processedDataRef.current?.isCfw)
            // Desktop's baseFetch returns the parsed body directly — no axios
            // `.data` envelope. Some backends still wrap fares in `data`,
            // so accept both shapes.
            const responseBody = response?.data ?? response ?? {}
            if (responseBody?.fares) {
                dispatch(mergeWowFares(responseBody.fares))
            }
        } catch (e) {
            console.error('Progressive WOW pricing fetch error:', e)
        } finally {
            dispatch(removeProgressiveLoadingIndices(indices))
        }
    }

    // ── Cards fingerprint — flips on filter/sort change ──────────────────
    const cardsFingerprint = useMemo(() => {
        return [
            cards.length,
            cards[0]?.travelOptionId || '',
            cards[1]?.travelOptionId || '',
            cards[2]?.travelOptionId || ''
        ].join('_')
    }, [cards])

    // Reset on filter/sort change (during render, before commit).
    const prevCardsFingerprintRef = useRef(cardsFingerprint)
    if (prevCardsFingerprintRef.current !== cardsFingerprint) {
        prevCardsFingerprintRef.current = cardsFingerprint
        indexElemMapRef.current = new Map()
        sentinelIndexRef.current =
            searchData?.configData?.progressiveWowSolutionsCount ??
            DEFAULT_PROGRESSIVE_BATCH_SIZE
        sentinelIORef.current?.disconnect()
        sentinelIORef.current = null
    }

    // After filter/sort: clear shimmer state + kick off window 0.
    useEffect(() => {
        if (!enabled) return
        dispatch(setProgressiveLoadingIndices([]))
        fetchWindowRef.current(0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardsFingerprint, enabled])

    // After initial WOW load completes: kick off window 0 in case the
    // cardsFingerprint effect ran while isDynamicContentLoading was true.
    useEffect(() => {
        if (!enabled) return
        if (!isDynamicContentLoading) {
            fetchWindowRef.current(0)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDynamicContentLoading, enabled])

    // Cleanup on unmount.
    useEffect(
        () => () => {
            sentinelIORef.current?.disconnect()
        },
        []
    )

    // ── makeCardRef ───────────────────────────────────────────────────────
    // The TuppleRenderer attaches this as the outermost container ref of each
    // card. We track which DOM element backs each index and reattach the IO
    // whenever the sentinel index's element (re)mounts.
    const makeCardRef = useCallback(
        (_travelOptionId: string, _cardId: string, index: number) =>
            (el: HTMLElement | null) => {
                if (!enabled) return
                if (!el) {
                    if (index === sentinelIndexRef.current) {
                        sentinelIORef.current?.disconnect()
                        sentinelIORef.current = null
                    }
                    return
                }
                indexElemMapRef.current.set(index, el)
                if (index === sentinelIndexRef.current) {
                    attachSentinelRef.current(index)
                }
            },
        [enabled]
    )

    return { makeCardRef }
}

export default useProgressiveWowPricing
